import React, { Component } from 'react';
import { Form, Input, Card } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import _first from 'lodash/first';
import _find from 'lodash/find';
import type { MerchantData, MoreDataAuthResponse, RequiredOTPResponse } from '@bringg/types';
import Button from '../button/button';
import Select, { Option } from '../select/select';
import TwoFactorAuthentication from 'components/two-factor-authentication/two-factor-authentication';

export interface LoginResponse {
	success: boolean;
	data: any;
}

interface Props {
	onLogin: (
		email: string,
		password: string,
		selectedMerchant?: MerchantData,
		captcha?: string
	) => Promise<LoginResponse>;
	footer?: React.ReactNode;
	onLoggedIn: () => void;
	recaptchaSiteKey: string;
	onLoginFailed: (e?: any) => void;
	passwordPlaceholder?: string;
	emailPlaceholder: string;
	invalidEmailText?: string;
	invalidPasswordText?: string;
	loginTitle: string;
	signInText: string;
	minPasswordLength?: number;
	onVerify: (code: string) => Promise<RequiredOTPResponse | LoginResponse>;
	onResend: () => Promise<RequiredOTPResponse>;
	getOptTranslations?: (email: string, minutesToExpiration: number) => Record<string, string>;
}

interface State {
	loggingIn: boolean;
	recaptchaSToken: string | null;
	merchantOptions?: MerchantData[];
	selectedMerchant?: MerchantData;
	showOtpForm: boolean;
	email: string;
	captchaValue?: string;
	otpExpiration?: Date | string;
}

export class Login extends Component<Props, State> {
	state = {
		loggingIn: false,
		recaptchaSToken: null,
		captchaValue: undefined,
		merchantOptions: undefined,
		selectedMerchant: undefined,
		showOtpForm: false,
		otpExpiration: undefined,
		email: ''
	};

	onLogin = async (email: string, password: string) => {
		try {
			const response = await this.props.onLogin(
				email,
				password,
				this.state.selectedMerchant,
				this.state.captchaValue
			);

			if (response.success && !(response.data.recaptcha_stoken || response.data.merchants_data)) {
				this.props.onLoggedIn();
			} else {
				const data = response.data as Partial<MoreDataAuthResponse>;

				if (data.recaptcha_stoken) {
					this.setState({
						recaptchaSToken: data.recaptcha_stoken,
						loggingIn: false
					});
				} else if (data.merchants_data) {
					this.setState({
						merchantOptions: data.merchants_data,
						recaptchaSToken: undefined,
						selectedMerchant: _first(data.merchants_data),
						loggingIn: false
					});
				}
			}
		} catch (e: any) {
			if (e.reason && e.reason.name === 'OTP_REQUIRED') {
				this.setState({
					showOtpForm: true,
					otpExpiration: e.data.expiration
				});
				return;
			}

			this.props.onLoginFailed(e);
			this.setState({
				recaptchaSToken: null,
				selectedMerchant: null,
				loggingIn: false
			});
		}
	};

	onClick = async ({ email, password }: any) => {
		this.setState({ loggingIn: true, email });

		await this.onLogin(email, password);
	};

	handleFailedSubmit = () => {
		this.setState({ loggingIn: false });
	};

	storeCaptchaResponse = captchaValue => {
		this.setState({ captchaValue });
	};

	onMerchantSelect = selectedMerchantUUID => {
		this.setState({ selectedMerchant: _find(this.state.merchantOptions, { uuid: selectedMerchantUUID }) });
	};

	handleVerifyOtp = async (code: string) => {
		try {
			const response = await this.props.onVerify(code);

			if (response.success) {
				// On successful verification, call onLoggedIn to route the user
				this.props.onLoggedIn();
				return response as LoginResponse;
			}

			return response;
		} catch (error) {
			return {
				success: false,
				data: { error }
			};
		}
	};

	handleResendOtp = async () => {
		const response = await this.props.onResend();
		this.setState({ otpExpiration: response.expiration });
		return response;
	};

	render() {
		const { loggingIn } = this.state;
		const {
			footer,
			recaptchaSiteKey,
			loginTitle,
			invalidEmailText,
			emailPlaceholder,
			invalidPasswordText,
			passwordPlaceholder,
			signInText,
			minPasswordLength,
			getOptTranslations
		} = this.props;

		return (
			<div className="login-page-wrapper">
				<div className="login-card">
					{this.state.showOtpForm ? (
						<TwoFactorAuthentication
							onVerify={this.handleVerifyOtp}
							onResend={this.handleResendOtp}
							expirationTime={this.state.otpExpiration}
							getTranslations={getOptTranslations}
							email={this.state.email}
						/>
					) : (
						<Card>
							<div className="login-header">
								<div className="login-title">
									<div>{loginTitle}</div>
									<div className="black-block" />
								</div>
							</div>
							<div className="login-body">
								<Form
									onFinish={this.onClick}
									onFinishFailed={this.handleFailedSubmit}
									validateTrigger="onSubmit"
								>
									<Form.Item
										name="email"
										rules={[{ type: 'email', message: invalidEmailText, required: true }]}
									>
										<Input
											data-testid="email"
											data-test-id="email"
											className="input"
											placeholder={emailPlaceholder}
										/>
									</Form.Item>
									<Form.Item
										name="password"
										rules={[
											{
												min: minPasswordLength,
												message: invalidPasswordText,
												required: true
											}
										]}
									>
										<Input
											data-testid="password"
											data-test-id="password"
											className="input"
											type="password"
											placeholder={passwordPlaceholder}
										/>
									</Form.Item>

									{this.state.recaptchaSToken ? (
										<ReCAPTCHA
											data-testid="captcha"
											data-test-id="captcha"
											onChange={this.storeCaptchaResponse}
											sitekey={recaptchaSiteKey}
										/>
									) : null}

									{this.state.merchantOptions ? (
										<Select
											data-testid="merchant-options"
											data-test-id="merchant-options"
											value={this.state.selectedMerchant.uuid}
											allowClear={false}
											onChange={this.onMerchantSelect}
											className="input multi-merchant"
										>
											{this.state.merchantOptions.map(merchant => (
												<Option value={merchant.uuid} key={merchant.uuid}>
													{merchant.name}
												</Option>
											))}
										</Select>
									) : null}
									<Button
										className="login-button"
										data-testid="login-button"
										data-test-id="login-button"
										htmlType="submit"
										type="primary"
										disabled={loggingIn}
										loading={loggingIn}
									>
										{signInText}
									</Button>
								</Form>
							</div>
						</Card>
					)}
				</div>
				{footer && <div className="login-footer">{footer}</div>}
			</div>
		);
	}
}

export default Login;
